// What a new enquiry cannot be saved without, and what each field has to
// look like once it holds anything. The rules shared with the client form —
// phone, email, name casing — come from `lib/fieldFormat`; what is here is
// the rest. The mirror of the clients feature's own `validation.ts`.
//
// The red asterisk each label carries is drawn from this list — so a field
// moved between the two below changes what the form says as well as what it
// enforces.
//
// What has to be filled in is the same floor the public enquiry form asks a
// stranger for, and no more: a name, who they are, what they want, and a way
// of reaching them. See `publicEnquiryValidation`, whose own `REQUIRED_FIELDS`
// is the list this one is kept level with.
//
// Everything the dashboard asks beyond that — a title, a date of birth, a
// profession, where the enquiry came from, the postal half of an address, a
// WhatsApp number apart from the phone — is worth having and is not worth
// stopping a save over. This form is very often filled in mid-call, and half
// of what it asks is not something the person on the other end has said yet.
//
// Title and DOB carry a second cost that the others do not, and it is paid in
// `enquiryPayload` rather than here: the endpoint takes a title as a catalog
// id and a birthday as an ISO date, so neither can go up as the empty string
// the form holds. Both keys are left off the body instead. See
// `toCreateEnquiryPayload`.

import {
  digitsOf,
  emailError,
  mobileError,
  pincodeError,
} from "../../lib/fieldFormat";
import { NewEnquiry } from "./types/enquiries.types";

export const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "gender",
  "lifestyle",
  "city",
  "phone",
  "email",
] as const;

/**
 * Asked for, checked once answered, and never a reason the form will not save.
 *
 * Five of these carry no rule of their own and are here only so the form can
 * still address them: `ValidatedField` is what `controlId` and `errorFor` are
 * keyed on, so a field in neither list is one the form cannot point at.
 * `fieldError` has no case for them, which is the same as saying anything
 * typed into them is fine.
 *
 * The other three do have rules, and that is the point of the distinction: a
 * blank DOB saves, and 31/02/1990 still does not.
 */
export const OPTIONAL_VALIDATED_FIELDS = [
  "title",
  "dob",
  "profession",
  "enquiryFrom",
  "state",
  "pincode",
  "whatsapp",
] as const;

/**
 * Fields holding a list rather than a line of text, and which cannot be left
 * empty. An enquiry is raised about something, so at least one goal has to be
 * picked — several may be, which is what puts the field here rather than among
 * the single answers above. The mirror of the client form's own.
 */
export const REQUIRED_LIST_FIELDS = ["goals"] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];
export type OptionalValidatedField = (typeof OPTIONAL_VALIDATED_FIELDS)[number];
export type RequiredListField = (typeof REQUIRED_LIST_FIELDS)[number];
export type ValidatedField =
  | RequiredField
  | OptionalValidatedField
  | RequiredListField;

export const VALIDATED_FIELDS = [
  ...REQUIRED_FIELDS,
  ...OPTIONAL_VALIDATED_FIELDS,
] as const;

export type NewEnquiryErrors = Partial<Record<ValidatedField, string>>;

/** Names used in "<field> is required", so the message reads like the label. */
const LABELS: Record<ValidatedField, string> = {
  title: "Title",
  firstName: "First name",
  lastName: "Last name",
  gender: "Gender",
  dob: "DOB",
  profession: "Profession",
  lifestyle: "Life style",
  goals: "Goal",
  enquiryFrom: "Enquiry from",
  state: "State",
  city: "City",
  pincode: "Pincode",
  phone: "Phone number",
  whatsapp: "WhatsApp number",
  email: "Email address",
};

/** A person's name: letters, plus the joiners that appear inside one. */
const NAME_PATTERN = /^[A-Za-z][A-Za-z\s.'-]*[A-Za-z.]$|^[A-Za-z]$/;

const DOB_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Nobody enquiring about a nutrition plan was born more than this long ago. */
export const MAX_AGE_YEARS = 120;

/**
 * A date of birth, checked against the calendar rather than just the shape:
 * 31/02 matches the pattern perfectly and is still not a day.
 */
const dobError = (value: string): string => {
  const match = DOB_PATTERN.exec(value);
  if (!match) return "Enter the date as DD/MM/YYYY";

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "That date does not exist";
  }

  const today = new Date();
  if (date > today) return "Date of birth cannot be in the future";
  if (year < today.getFullYear() - MAX_AGE_YEARS)
    return "Enter a valid date of birth";

  return "";
};

const isRequired = (field: ValidatedField): field is RequiredField =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (field: ValidatedField, raw: string): string => {
  const value = raw.trim();

  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    case "firstName":
    case "lastName":
      return NAME_PATTERN.test(value)
        ? ""
        : `${LABELS[field]} can only contain letters`;
    case "dob":
      return dobError(value);
    case "pincode":
      return pincodeError(value);
    case "phone":
    case "whatsapp":
      return mobileError(LABELS[field], value);
    case "email":
      return emailError(value);
    default:
      return "";
  }
};

/**
 * The list fields that have to hold something. An empty one reads as the same
 * "<field> is required" a blank dropdown does, so the form says the same thing
 * about a goal whether it takes one answer or several.
 */
export const listFieldError = (
  field: RequiredListField,
  values: string[]
): string => (values.length === 0 ? `${LABELS[field]} is required` : "");

/** Only failing fields appear, so an empty object means the form is valid. */
export const validateNewEnquiry = (
  enquiry: NewEnquiry
): NewEnquiryErrors => {
  const errors: NewEnquiryErrors = {};

  VALIDATED_FIELDS.forEach((field) => {
    const message = fieldError(field, enquiry[field]);
    if (message) errors[field] = message;
  });

  REQUIRED_LIST_FIELDS.forEach((field) => {
    const message = listFieldError(field, enquiry[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

/* ── Input normalisers ─────────────────────────────────────────────────── */

/** Types the slashes for you — see the client form's `normaliseDobInput`. */
export const normaliseDobInput = (raw: string): string => {
  const digits = digitsOf(raw).slice(0, 8);

  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join("/");
};

/** Letters and the joiners inside a name; anything else never lands. */
export const normaliseNameInput = (raw: string): string =>
  raw.replace(/[^A-Za-z\s.'-]/g, "");
